import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { SIMPLIFIED_FOOTER_URLS, SIMPLIFIED_HEADER_URLS } from '~app/modules/generic/constants';
import {
  APP_CART_ROUTE,
  CHECKOUT_ROUTE,
  FeatureToggleEnum,
  NUMBER_1,
  YOUR_ASSORTMENT_EXTERNAL,
} from '~shared/constants';
import { SOURCES_CONFIG } from '~shared/constants/sources.config';
import { AccountType, IAccountInformation } from '~shared/models';
import { MediaAliasMap } from '~shared/models/media.model';
import { ElementVisibilityInViewportApplicationEvent } from '~shared/modules/application-event/models';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { CookieDisclaimerService } from '~shared/modules/cookie-consent/cookie-consent.module';
import { GaTrackingEventLabel, GATrackingEventType } from '~shared/modules/ga-tracking/constants';
import { GATrackingService } from '~shared/modules/ga-tracking/services';
import { getIsUserBlocked, getUserData } from '~shared/modules/user-info/store';
import { MediaService } from '~shared/services/media.service';
import { MetaInfoService } from '~shared/services/meta-info.service';
import { OptimizeExperimentService } from '~shared/services/optimize-experiment.service';
import { PageService } from '~shared/services/page.service';
import { SourcesInsertService } from '~shared/services/sources-insert.service';
import { TitleService } from '~shared/services/title.service';
import { PageDetectionUtil } from '~shared/utils/page-detection.util';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { OptimizeActivationEventService } from '~shared/services/optimize-activation-event.service';
import { MediaObserver } from '@angular/flex-layout';
import { filter, map, startWith, take, takeUntil } from 'rxjs/operators';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { IdentityService } from '~core/modules/identity/services/identity.service';
import { CookieService } from '~shared/services/cookie.service';
import { PRICE_TYPE_EXPLANATION_COOKIE_NAME } from '~shared/modules/price-type-toggler/constants/cookie-name.constant';
import { StoryblokService } from './modules/storyblok/services';
import { BlackDealsFeatureFlagService } from '~shared/services/black-deals-feature-flag.service';
import * as fromStoryblok from './modules/storyblok/store';
import { OptimizelyService } from '~shared/services/optimizely.service';
import { UrlUtils } from './modules/generic/utils/url.utils';
import { TranslateService } from '@ngx-translate/core';
import { MmAlertMode, MmAlertService, MmAlertSize, MmAlertType } from '@metromarkets/components';
import {
  ACCOUNT_TYPE_KEY,
  IMPERSONATED_CUSTOMER_FULLNAME_KEY,
  SUBSIDIARY_INITIAL_REDIRECTION_STATUS,
} from './core/modules/identity/constants/identity.constant';
import * as fromFilters from './modules/filters/store';
import * as fromCatalog from './modules/catalog/store';
import { getActiveUrl, getQueryParams, RouterState } from '~core/store';
import { ProductsLoadingService } from '~app/modules/catalog/services/products-loading.service';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { PlpVariantRestyleService } from '~shared/services/plp-variant-restyle/plp-variant-restyle.service';
import { AuthorizationIdamStateService } from './modules/authorization/services/authorization-idam-state.service';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { MarketDetectionUtil } from '~shared/utils/market-detection.util';
import { AccountInfoService } from './core/modules/identity/services/account-info.service';

declare const hj;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('focusReset', { static: true }) focusReset!: ElementRef;
  public isFooterSimplified: boolean;
  public isHeaderSimplified: boolean;
  public isHotjarAllowedRoute: boolean;
  public classNameList: string[];
  isUserBlocked$ = this.accountInformationStore.pipe(select(getIsUserBlocked));
  isPriceTypeExplanationCookieExist = true;
  isMetroxRoutes = false;
  showMobileNavigation = false;
  isCheckoutPage = false;
  isEmployeeRestrictedView = false;
  footerTrackingPageType = null;
  isPdP: boolean;
  loadFooter: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isStoryblokPreview = false;
  routeChangeEmission = new EventEmitter<void>();
  userInfo$: Observable<IAccountInformation> = this.accountInformationStore.pipe(select(getUserData));
  isSubsidiaryAccount$: Observable<boolean>;
  public isLoggedIn$: Observable<boolean>;
  public isMobileOrTablet: boolean;
  isZeroResult$: Observable<boolean>;
  hasFilters$: Observable<boolean>;
  isSubsidiaryRedirectionEnabled$: Observable<boolean> = this.featureFlagService.isFeatureEnabledAndReadyV2(
    FeatureToggleEnum.FF_SL_ENABLE_SUBSIDIARY_REDIRECTION
  );
  isPLP: boolean;
  isNewPLPVariantRestyle$: Observable<boolean>;
  @ViewChild('appRef') appRef: ElementRef;
  @ViewChild('cookieConsentRef') cookieConsentRef: ElementRef<HTMLElement>;
  private mediaClassNameList: string[];
  private destroySubject$ = new Subject<boolean>();

  constructor(
    private optimizeExperimentService: OptimizeExperimentService,
    private readonly router: Router,
    private readonly mediaService: MediaService,
    private readonly pageService: PageService,
    private readonly titleService: TitleService,
    private readonly cookieDisclaimerService: CookieDisclaimerService,
    private readonly metaInfoService: MetaInfoService,
    private readonly sourceInsertService: SourcesInsertService,
    private gaTrackingService: GATrackingService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private applicationEventService: ApplicationEventService,
    private optimizeActivationEventService: OptimizeActivationEventService,
    private mediaObserver: MediaObserver,
    private identityService: IdentityService,
    private cookieService: CookieService,
    private optimizelyService: OptimizelyService,
    private storyblokService: StoryblokService,
    private blackDealsFeatureFlagService: BlackDealsFeatureFlagService,
    private translateService: TranslateService,
    private alertService: MmAlertService,
    private filtersStore: Store<fromFilters.State>,
    private catalogStore: Store<fromCatalog.CatalogState>,
    private productsLoadingService: ProductsLoadingService,
    private featureFlagService: ActiveFeaturesConfigcatService,
    private accountInformationStore: Store<IAccountInformation>,
    private store: Store<RouterState>,
    private plpVariantRestyleService: PlpVariantRestyleService,
    private authorizationIdamStateService: AuthorizationIdamStateService,
    private accountInfoService: AccountInfoService,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any
  ) {
    this.isLoggedIn$ = this.identityService.isLoggedIn();
    this.blackDealsFeatureFlagService.initIsBlackDealsEnabledFlag();
    if (isPlatformBrowser(this.platformId)) {
      this.optimizeExperimentService.loadAntiFlickerScript();
      this.gaTrackingService.startTracking();
    }
    this.mediaService.mediaAliases$.pipe(takeUntil(this.destroySubject$)).subscribe(this.handleMediaChange.bind(this));

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(this.router),
        takeUntil(this.destroySubject$)
      )
      .subscribe(this.handleRouterChange.bind(this));
    this.isStoryblokPreview = this.storyblokService.isPreviewMode();
    MarketDetectionUtil.initializeRequestFromMain(this.webAppBuyerRequest);
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.store.pipe(select(getActiveUrl), takeUntil(this.destroySubject$)).subscribe((url: string) => {
        this.isPLP = UrlUtils.isProductListPage(url);
      });
      this.isPriceTypeExplanationCookieExist = !!this.cookieService.get(PRICE_TYPE_EXPLANATION_COOKIE_NAME);

      if (!this.isStoryblokPreview) {
        // Initializing MetroDigital Cookie Disclaimer
        this.cookieDisclaimerService.initialize();
      }

      this.addMetaInfo();
      // Load General Resources
      this.sourceInsertService.setAll(SOURCES_CONFIG());
      this.optimizelyService.initialize();
      window['isAngularApp'] = true;

      const queryParams$ = this.filtersStore.pipe(select(getQueryParams));
      this.hasFilters$ = queryParams$.pipe(
        map((queryParams) => {
          const filters = queryParams.filter || {};
          return filters.options || filters.price || filters.promotion;
        })
      );
      this.isNewPLPVariantRestyle$ = this.plpVariantRestyleService.isNewPLPVariantRestyle();

      this.featureFlagService
        .isFeatureEnabledReadyForCurrentCountry$('FF_CCS_GENESYS_ACTIVATION')
        .pipe(takeUntil(this.destroySubject$))
        .subscribe((isEnabled: boolean) => {
          window['CCS_COBROWSE'] = isEnabled;
        });
    }

    // reset focus to skip to menu for a11y purposes
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      // Delay to ensure DOM is rendered
      setTimeout(() => {
        this.focusReset.nativeElement.focus();
      }, 0);
    });

    // Storyblok content reloading
    this.storyblokService.checkContentReload();

    // Storyblok load global data once
    this.accountInformationStore.dispatch(new fromStoryblok.LoadStoryblokGlobalContent());
    this.handleSubsidiaryRedirection();
    this.isSubsidiaryAccount$ = this.accountInfoService.isSubsidiaryAccount();
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.onActiveImpersonationAlert();
      this.mediaObserver
        .asObservable()
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(() => {
          const isTablet = this.mediaObserver.isActive(MEDIA_ALISES.tablet);
          this.isMobileOrTablet = this.mediaObserver.isActive(MEDIA_ALISES.mobile) || isTablet;
          const isDesktop = this.mediaObserver.isActive(MEDIA_ALISES.desktop);
          if (isTablet) {
            // Important to fire any click/touch event on tablets
            this.appRef.nativeElement.addEventListener('click', () => {});
          }
          this.showMobileNavigation = !isDesktop;
        });
      if (!this.isStoryblokPreview) {
        this.cookieDisclaimerService.listenForUserAcceptance(this.cookieConsentRef);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  onIsVisible() {
    if (this.footerTrackingPageType) {
      this.applicationEventService.publish(
        new ElementVisibilityInViewportApplicationEvent(this.footerTrackingPageType, GaTrackingEventLabel.Footer)
      );
    }
  }

  private addMetaInfo() {
    this.metaInfoService.addTag(
      {
        name: 'description',
        content: 'CORE.ALL_PAGES.META_DESCRIPTION',
      },
      true
    );
  }

  private handleSubsidiaryRedirection() {
    const isNormalLogin$ = this.authorizationIdamStateService.isNormalLogin();
    combineLatest([this.isSubsidiaryRedirectionEnabled$, this.accountInfoService.isSubsidiaryAccount(), isNormalLogin$])
      .pipe(take(1))
      .subscribe(([isEnabled, isSubsidiary, isNormalLogin]) => {
        if (isEnabled && isSubsidiary && isNormalLogin) {
          localStorage.setItem(SUBSIDIARY_INITIAL_REDIRECTION_STATUS, 'true');
          window.location.href = YOUR_ASSORTMENT_EXTERNAL();
        }
      });
  }

  private handleMediaChange(mediaAliases: MediaAliasMap): void {
    this.mediaClassNameList = Object.keys(mediaAliases).filter((mediaAlias) => mediaAliases[mediaAlias]);

    this.updateClassNameList(this.router.url);
  }

  private handleRouterChange(event): void {
    this.routeChangeEmission.emit();
    this.routerEventNavigationEnd(event);
  }

  private routerEventNavigationEnd(event): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isEmployeeRestrictedView = UrlUtils.getIsEmployeeRestrictedView(event.url);
      this.isZeroResult$ = combineLatest([
        this.catalogStore.pipe(select(fromCatalog.getProducts)),
        this.productsLoadingService.isProductsLoading$,
      ]).pipe(
        map(
          ([products, isLoadingProducts]) =>
            UrlUtils.isSearchPage(this.router.url) && !isLoadingProducts && products?.length < 1
        )
      );
    }

    const pageUrl = event.url;
    this.optimizeActivationEventService.triggerActivationEvent(pageUrl);
    this.loadFooter.next(true);
    this.updateHeaderAndFooterVisibility(pageUrl);
    this.updateClassNameList(pageUrl);
    this.updateHotjarVisibility(pageUrl);

    this.titleService.updatePageTitleFromRoute();
  }

  private updateHeaderAndFooterVisibility(pageUrl): void {
    this.isPdP = false;
    this.isMetroxRoutes =
      PageDetectionUtil.isHomePage(pageUrl) ||
      PageDetectionUtil.isProductListPage(pageUrl) ||
      PageDetectionUtil.isProductDetailsPage(pageUrl) ||
      PageDetectionUtil.isCustomCategoryHubPage(pageUrl) ||
      PageDetectionUtil.isLandingPagePage(pageUrl) ||
      PageDetectionUtil.isBrandPLPPage(pageUrl);

    this.isCheckoutPage = PageDetectionUtil.isCheckoutPage(pageUrl) && !pageUrl.includes(`/${APP_CART_ROUTE}`);

    let currentSimplifiedHeaderUrls = [...SIMPLIFIED_HEADER_URLS];
    if (this.isEmployeeRestrictedView) {
      currentSimplifiedHeaderUrls = currentSimplifiedHeaderUrls.filter(
        (url) => url !== `/${CHECKOUT_ROUTE}/${CHECKOUT_ROUTE}`
      );
    }
    this.isFooterSimplified = SIMPLIFIED_FOOTER_URLS.some((path) => pageUrl.startsWith(path));
    this.isHeaderSimplified = currentSimplifiedHeaderUrls.some((path) => pageUrl.startsWith(path));

    if (PageDetectionUtil.isHomePage(pageUrl)) {
      this.footerTrackingPageType = GATrackingEventType.HomepageScrolling;
    }
    if (PageDetectionUtil.isProductDetailsPage(pageUrl)) {
      this.footerTrackingPageType = GATrackingEventType.PdpScrolling;

      if (isPlatformBrowser(this.platformId)) {
        window.scrollTo(0, 0);
      }

      this.isPdP = true;
    }
  }

  private updateClassNameList(pageUrl): void {
    const pageAlias = this.pageService.getPageAlias(pageUrl);
    const pageClassName = pageAlias ? `page-${pageAlias}` : '';

    this.classNameList = [
      ...(this.mediaClassNameList ? this.mediaClassNameList : []),
      pageClassName,
      this.isMetroxRoutes ? 'app__metrox' : '',
    ];
  }

  private updateHotjarVisibility(pageUrl): void {
    this.isHotjarAllowedRoute =
      PageDetectionUtil.isHomePage(pageUrl) ||
      PageDetectionUtil.isProductListPage(pageUrl) ||
      PageDetectionUtil.isProductDetailsPage(pageUrl) ||
      PageDetectionUtil.isCartPage(pageUrl) ||
      PageDetectionUtil.isSignupPage(pageUrl) ||
      PageDetectionUtil.isUserEmailValidationPage(pageUrl) ||
      PageDetectionUtil.isCheckoutPage(pageUrl) ||
      PageDetectionUtil.isBuyAgainPage(pageUrl);
  }

  private onActiveImpersonationAlert(): void {
    if (
      isPlatformBrowser(this.platformId) &&
      localStorage.getItem(ACCOUNT_TYPE_KEY) === AccountType.EMPLOYEE_AS_CUSTOMER
    ) {
      const title = 'PAGES.AUTH.EMPLOYEE.ALERT.IMPERSONATION_STILL_ACTIVE.TITLE';
      const message = 'PAGES.AUTH.EMPLOYEE.ALERT.IMPERSONATION_STILL_ACTIVE.CONTENT';
      this.translateService
        .get([title, message], { user: `"${localStorage.getItem(IMPERSONATED_CUSTOMER_FULLNAME_KEY) ?? ''}"` })
        .pipe(take(NUMBER_1))
        .subscribe((translations) => {
          this.alertService.addAlert({
            type: MmAlertType.warning,
            title: translations[title],
            message: translations[message],
            mode: MmAlertMode.banner,
            size: MmAlertSize.large,
          });
        });
    }
  }
}
